import React, { useEffect, useState } from "react";
import {
  PenLine, FileText, FileDown, Link2, GitCompare, LineChart, Worm,
  Loader2, Sparkles, ExternalLink, Trash2, Plus, X, Wallet
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, API } from "../../lib/api";

const TABS = [
  { id: "content",    label: "Content Writer", icon: PenLine },
  { id: "blog",       label: "Blog Generator", icon: FileText },
  { id: "report",     label: "Report Builder", icon: FileDown },
  { id: "backlinks",  label: "Backlinks",      icon: Link2 },
  { id: "competitor", label: "Competitor",     icon: GitCompare },
  { id: "tracker",    label: "Rank Tracker",   icon: LineChart },
  { id: "crawler",    label: "Site Crawler",   icon: Worm },
];

export default function SEOSuite() {
  const [tab, setTab] = useState(() => localStorage.getItem("optivize_seo_suite_tab") || "content");
  const [wallet, setWallet] = useState(null);

  useEffect(() => { localStorage.setItem("optivize_seo_suite_tab", tab); }, [tab]);
  useEffect(() => {
    api.get("/credits/me").then(r => setWallet(r.data)).catch(() => {});
  }, [tab]);

  const refreshWallet = () => api.get("/credits/me").then(r => setWallet(r.data)).catch(() => {});

  return (
    <div className="space-y-6" data-testid="seo-suite-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3" style={{fontFamily:'Outfit'}}>
            <Sparkles className="text-blue-400" /> SEO Suite
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Content, reports, link intelligence, competitor analysis, rank tracking & site crawler — all in one workbench.</p>
        </div>
        <Link to="/dashboard/credits" className="card px-4 py-2.5 flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="seo-suite-credits-balance">
          <Wallet size={15} className="text-blue-400" />
          <span className="font-semibold text-white">{wallet?.balance ?? "—"}</span> credits
        </Link>
      </header>

      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="seo-suite-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`seo-suite-tab-${t.id}`}
            className={`whitespace-nowrap px-3.5 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
              tab === t.id
                ? "bg-blue-600 text-white shadow-[0_6px_20px_-6px_rgba(37,99,235,0.6)]"
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>

      {tab === "content"    && <ContentWriter   onSpent={refreshWallet} />}
      {tab === "blog"       && <BlogGenerator   onSpent={refreshWallet} />}
      {tab === "report"     && <ReportBuilder   onSpent={refreshWallet} />}
      {tab === "backlinks"  && <BacklinksPanel  onSpent={refreshWallet} />}
      {tab === "competitor" && <CompetitorPanel onSpent={refreshWallet} />}
      {tab === "tracker"    && <RankTracker     onSpent={refreshWallet} />}
      {tab === "crawler"    && <SiteCrawler     onSpent={refreshWallet} />}
    </div>
  );
}

// ============================================================================
// CONTENT WRITER
// ============================================================================
function ContentWriter({ onSpent }) {
  const [mode, setMode] = useState("brief");
  const [topic, setTopic] = useState("");
  const [kw, setKw] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState(600);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const MODE_COST = { brief: 2, outline: 3, full: 8 };

  const run = async () => {
    if (topic.trim().length < 4) { toast.error("Topic must be 4+ chars"); return; }
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/seo/content/generate", {
        topic: topic.trim(), mode, target_keyword: kw.trim() || undefined,
        tone, length_words: Number(length),
      });
      setOut(data);
      toast.success(`${mode} ready`);
      onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-content-panel">
      <div className="card p-5 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {["brief","outline","full"].map(m => (
            <button key={m} onClick={() => setMode(m)}
                    data-testid={`content-mode-${m}`}
                    className={`px-3 py-1.5 rounded-full text-xs ${mode===m?"bg-blue-600 text-white":"bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              {m === "brief" ? "Brief" : m === "outline" ? "Outline" : "Full article"} · {MODE_COST[m]} cr
            </button>
          ))}
        </div>
        <input value={topic} onChange={e=>setTopic(e.target.value)} placeholder="Topic e.g. 'how AI website builders compare in 2026'"
               className="input w-full" data-testid="content-topic"/>
        <div className="grid sm:grid-cols-3 gap-3">
          <input value={kw} onChange={e=>setKw(e.target.value)} placeholder="Target keyword (optional)"
                 className="input" data-testid="content-keyword"/>
          <select value={tone} onChange={e=>setTone(e.target.value)} className="input" data-testid="content-tone">
            {["professional","casual","persuasive","technical","friendly"].map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={length} onChange={e=>setLength(e.target.value)} className="input" data-testid="content-length">
            {[300, 600, 900, 1500, 2000].map(n => <option key={n} value={n}>{n} words</option>)}
          </select>
        </div>
        <button onClick={run} disabled={busy} className="btn-primary" data-testid="content-generate-btn">
          {busy ? <><Loader2 className="animate-spin" size={15}/> Writing…</> : <>Generate · {MODE_COST[mode]} cr</>}
        </button>
      </div>

      {out && (
        <div className="card p-5 space-y-3" data-testid="content-result">
          {out.mode === "full" ? (
            <pre className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed font-sans">
              {out.result?.markdown || out.result?.raw || ""}
            </pre>
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-slate-200 bg-slate-950/50 p-3 rounded-lg overflow-auto">
              {JSON.stringify(out.result, null, 2)}
            </pre>
          )}
          <button
            onClick={() => navigator.clipboard.writeText(
              out.mode === "full" ? (out.result?.markdown || "") : JSON.stringify(out.result, null, 2)
            )}
            className="btn-ghost text-xs"
            data-testid="content-copy-btn"
          >Copy</button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// BLOG GENERATOR
// ============================================================================
function BlogGenerator({ onSpent }) {
  const [topic, setTopic] = useState("");
  const [kw, setKw] = useState("");
  const [category, setCategory] = useState("Insights");
  const [length, setLength] = useState(700);
  const [autoPub, setAutoPub] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [pubBusy, setPubBusy] = useState(null);

  const load = () => api.get("/seo/blog/drafts/me").then(r => setDrafts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (topic.trim().length < 4) { toast.error("Topic too short"); return; }
    setBusy(true);
    try {
      await api.post("/seo/blog/generate", {
        topic: topic.trim(), target_keyword: kw.trim() || undefined,
        category, length_words: Number(length), auto_publish: autoPub,
      });
      toast.success(autoPub ? "Generated & published!" : "Draft generated");
      setTopic(""); setKw("");
      load(); onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const publish = async (draftId) => {
    setPubBusy(draftId);
    try {
      const { data } = await api.post("/seo/blog/publish", { draft_id: draftId });
      toast.success(`Published! View at ${data.public_url}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPubBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="seo-blog-panel">
      <div className="card p-5 space-y-3">
        <input value={topic} onChange={e=>setTopic(e.target.value)} placeholder="Topic for the blog post"
               className="input w-full" data-testid="blog-topic"/>
        <div className="grid sm:grid-cols-3 gap-3">
          <input value={kw} onChange={e=>setKw(e.target.value)} placeholder="Target keyword (optional)" className="input" data-testid="blog-keyword"/>
          <input value={category} onChange={e=>setCategory(e.target.value)} placeholder="Category" className="input" data-testid="blog-category"/>
          <select value={length} onChange={e=>setLength(e.target.value)} className="input" data-testid="blog-length">
            {[400, 700, 1000, 1500, 2000].map(n => <option key={n} value={n}>{n} words</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={autoPub} onChange={e=>setAutoPub(e.target.checked)} data-testid="blog-autopub"/>
          Auto-publish to <code className="text-blue-300">/blog</code> immediately (10 cr instead of 8)
        </label>
        <button onClick={generate} disabled={busy} className="btn-primary" data-testid="blog-generate-btn">
          {busy ? <><Loader2 className="animate-spin" size={15}/> Writing…</> : <>Generate · {autoPub ? 10 : 8} cr</>}
        </button>
      </div>

      {drafts.length > 0 && (
        <div className="card p-5">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-3">Drafts</h3>
          <ul className="divide-y divide-slate-800" data-testid="blog-drafts">
            {drafts.map(d => (
              <li key={d.id} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.excerpt || d.topic}</div>
                </div>
                {d.published ? (
                  <a href={d.public_url} target="_blank" rel="noreferrer"
                     className="text-emerald-300 text-xs flex items-center gap-1 hover:text-emerald-200">
                    Live <ExternalLink size={12}/>
                  </a>
                ) : (
                  <button onClick={()=>publish(d.id)} disabled={pubBusy === d.id}
                          className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`blog-publish-${d.id}`}>
                    {pubBusy === d.id ? <Loader2 className="animate-spin" size={13}/> : "Publish"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// REPORT BUILDER (PDF)
// ============================================================================
function ReportBuilder({ onSpent }) {
  const [audits, setAudits] = useState([]);
  const [kwruns, setKwruns] = useState([]);
  const [auditId, setAuditId] = useState("");
  const [kwId, setKwId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/seo/history?kind=analyze&limit=30").then(r => setAudits(r.data)).catch(() => {});
    api.get("/seo/history?kind=keywords&limit=30").then(r => setKwruns(r.data)).catch(() => {});
  }, []);

  const download = async () => {
    if (!auditId && !kwId) { toast.error("Pick an audit and/or keyword run"); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("optivize_token");
      const resp = await fetch(`${API}/seo/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          audit_run_id: auditId || undefined,
          keyword_run_id: kwId || undefined,
          project_name: projectName || undefined,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `optivize-seo-report.pdf`;
      a.click();
      toast.success("Report downloaded");
      onSpent();
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-3" data-testid="seo-report-panel">
        <input value={projectName} onChange={e=>setProjectName(e.target.value)} placeholder="Project name (e.g. 'Acme Corp Q1 audit')"
               className="input w-full" data-testid="report-project-name"/>
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={auditId} onChange={e=>setAuditId(e.target.value)} className="input" data-testid="report-audit-select">
            <option value="">— pick audit run —</option>
            {audits.map(a => (
              <option key={a.id} value={a.id}>{a.score}/100 · {a.url}</option>
            ))}
          </select>
          <select value={kwId} onChange={e=>setKwId(e.target.value)} className="input" data-testid="report-keyword-select">
            <option value="">— pick keyword run —</option>
            {kwruns.map(k => (
              <option key={k.id} value={k.id}>{k.seed} ({k.country})</option>
            ))}
          </select>
        </div>
        <button onClick={download} disabled={busy} className="btn-primary" data-testid="report-download-btn">
          {busy ? <><Loader2 className="animate-spin" size={15}/> Generating PDF…</> : <><FileDown size={15}/> Download branded PDF · 5 cr</>}
        </button>
        <p className="text-slate-500 text-xs">Combines your selected audit + keyword research into a single branded PDF you can hand to clients.</p>
      </div>

      <EmailReportCard auditId={auditId} kwId={kwId} projectName={projectName} onSpent={onSpent}/>
    </div>
  );
}

function EmailReportCard({ auditId, kwId, projectName, onSpent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [brandId, setBrandId] = useState("");
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/admin/brand/profiles").then(r => setProfiles(r.data || []))
      .catch(() => setProfiles([])); // non-admin users will get 403 — harmless
  }, []);

  const send = async () => {
    if (!auditId && !kwId) { toast.error("Pick an audit and/or keyword run above"); return; }
    if (!email.includes("@")) { toast.error("Enter a valid recipient email"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/seo/reports/email", {
        audit_run_id: auditId || undefined,
        keyword_run_id: kwId || undefined,
        project_name: projectName || undefined,
        recipient_email: email.trim(),
        recipient_name: name.trim() || undefined,
        invoice_amount_inr: amount ? Number(amount) : undefined,
        custom_message: msg.trim() || undefined,
        brand_profile_id: brandId || undefined,
      });
      setResult(data);
      toast.success(`Sent to ${email}${data.payment_link ? " with Razorpay link" : ""}`);
      onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-5 space-y-3 border-blue-500/30" data-testid="seo-report-email-panel">
      <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider">
        <FileDown size={13}/> One-click client delivery
      </div>
      <h3 className="text-xl font-bold text-white" style={{fontFamily:'Outfit'}}>Email report to client (+ optional invoice)</h3>
      <p className="text-slate-400 text-sm">
        Sends the branded PDF directly to your client. Add an INR amount to auto-mint a
        Razorpay payment link and persist a standalone invoice on your dashboard.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <input value={email} onChange={e=>setEmail(e.target.value)} type="email"
               placeholder="client@company.com" className="input" data-testid="report-email-to"/>
        <input value={name} onChange={e=>setName(e.target.value)}
               placeholder="Client name (optional)" className="input" data-testid="report-email-name"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={amount} onChange={e=>setAmount(e.target.value.replace(/[^0-9.]/g,""))}
               placeholder="Invoice amount INR (optional)" className="input" data-testid="report-email-amount"/>
        <input value={msg} onChange={e=>setMsg(e.target.value)}
               placeholder="Custom message (optional)" className="input" data-testid="report-email-msg"/>
      </div>

      {profiles.length > 0 && (
        <div>
          <label className="label">Send under brand</label>
          <select value={brandId} onChange={e=>setBrandId(e.target.value)}
                  className="input w-full" data-testid="report-email-brand-select">
            <option value="">Default Optivize branding</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.brand_name})</option>)}
          </select>
        </div>
      )}

      <button onClick={send} disabled={busy} className="btn-primary" data-testid="report-email-send-btn">
        {busy ? <><Loader2 className="animate-spin" size={15}/> Sending…</> :
                <>Send to client · 8 cr {amount ? "(+ Razorpay link)" : ""}</>}
      </button>

      {result && result.ok && (
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-200 space-y-1" data-testid="report-email-result">
          <div>✓ Delivered to <b>{result.to}</b></div>
          {result.invoice_number && <div>Invoice <code>{result.invoice_number}</code> created · ₹{result.amount_inr}</div>}
          {result.payment_link && (
            <a href={result.payment_link} target="_blank" rel="noreferrer" className="text-emerald-300 underline break-all block">
              {result.payment_link}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// BACKLINKS
// ============================================================================
function BacklinksPanel({ onSpent }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!url.trim()) { toast.error("Enter a URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/seo/backlinks/analyze", { url: url.trim() });
      setData(data.result);
      onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-backlinks-panel">
      <div className="card p-5 flex gap-3 flex-wrap items-center">
        <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="Domain or URL"
               className="input flex-1 min-w-[260px]" data-testid="backlinks-url"/>
        <button onClick={run} disabled={busy} className="btn-primary" data-testid="backlinks-run-btn">
          {busy ? <Loader2 className="animate-spin" size={15}/> : "Analyze · 4 cr"}
        </button>
      </div>

      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-5">
            <h4 className="text-white font-bold mb-3">Authority</h4>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Domain Rating" value={data.domain_rating ?? "—"}/>
              <Stat label="Referring domains" value={(data.referring_domains ?? 0).toLocaleString()}/>
              <Stat label="Total backlinks" value={(data.total_backlinks ?? 0).toLocaleString()}/>
              <Stat label="Organic traffic" value={(data.organic_traffic_est ?? 0).toLocaleString()}/>
              <Stat label="Dofollow share" value={`${Math.round((data.dofollow_share ?? 0)*100)}%`}/>
              <Stat label="Velocity" value={data.link_velocity_trend ?? "—"}/>
            </div>
          </div>
          <div className="card p-5">
            <h4 className="text-white font-bold mb-3">Top anchors</h4>
            <ul className="text-sm space-y-1.5">
              {(data.top_anchors || []).map((a, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="text-slate-200 truncate">{a.anchor}</span>
                  <span className="text-slate-400 font-mono">{a.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-5 md:col-span-2">
            <h4 className="text-white font-bold mb-3">Top referring domains</h4>
            <ul className="text-sm grid sm:grid-cols-2 gap-1.5">
              {(data.top_referring_domains || []).map((d, i) => (
                <li key={i} className="flex items-center justify-between gap-2 bg-slate-900/40 rounded-lg px-3 py-2">
                  <span className="text-slate-200 truncate">{d.domain}</span>
                  <span className="text-xs text-slate-500">{d.type}</span>
                  <span className="text-slate-400 font-mono text-xs">DR {d.dr}</span>
                </li>
              ))}
            </ul>
          </div>
          {(data.opportunities || []).length > 0 && (
            <div className="card p-5 md:col-span-2">
              <h4 className="text-white font-bold mb-3">Opportunities</h4>
              <ul className="space-y-1.5 text-sm">
                {data.opportunities.map((o, i) => (
                  <li key={i} className="text-slate-300 flex gap-2">
                    <Sparkles size={14} className="text-amber-400 mt-0.5 flex-shrink-0"/>{o}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-900/50 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-white font-bold text-lg mt-0.5">{value}</div>
    </div>
  );
}

// ============================================================================
// COMPETITOR
// ============================================================================
function CompetitorPanel({ onSpent }) {
  const [yourUrl, setYourUrl] = useState("");
  const [compUrl, setCompUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!yourUrl.trim() || !compUrl.trim()) { toast.error("Enter both URLs"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/seo/competitor/compare", {
        your_url: yourUrl.trim(), competitor_url: compUrl.trim(),
      });
      setData(data);
      onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-competitor-panel">
      <div className="card p-5 grid sm:grid-cols-2 gap-3">
        <input value={yourUrl} onChange={e=>setYourUrl(e.target.value)} placeholder="Your URL" className="input" data-testid="competitor-your-url"/>
        <input value={compUrl} onChange={e=>setCompUrl(e.target.value)} placeholder="Competitor URL" className="input" data-testid="competitor-comp-url"/>
        <button onClick={run} disabled={busy} className="btn-primary col-span-full" data-testid="competitor-run-btn">
          {busy ? <><Loader2 className="animate-spin" size={15}/> Comparing…</> : "Compare · 5 cr"}
        </button>
      </div>

      {data && (
        <div className="space-y-4" data-testid="competitor-result">
          <div className="grid sm:grid-cols-2 gap-3">
            <ScoreCard label="You" url={data.your_url} score={data.you_score} accent="bg-blue-500/15 border-blue-500/40"/>
            <ScoreCard label="Competitor" url={data.competitor_url} score={data.competitor_score} accent="bg-rose-500/15 border-rose-500/40"/>
          </div>
          {data.synthesis?.verdict && (
            <div className="card p-5">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Verdict</div>
              <div className="text-white font-semibold mt-1">{data.synthesis.verdict}</div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <Bucket title="Where you win" items={data.synthesis?.where_you_win} color="text-emerald-300"/>
            <Bucket title="Where they win" items={data.synthesis?.where_they_win} color="text-rose-300"/>
            <Bucket title="Quick wins" items={data.synthesis?.quick_wins} color="text-amber-300"/>
            <Bucket title="Content gaps" items={data.synthesis?.content_gaps} color="text-sky-300"/>
            <Bucket title="Technical gaps" items={data.synthesis?.tech_gaps} color="text-violet-300"/>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreCard({ label, url, score, accent }) {
  return (
    <div className={`p-5 rounded-xl border ${accent}`}>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-white text-sm truncate">{url}</div>
      <div className="text-4xl font-black text-white mt-2">{score}<span className="text-base text-slate-400">/100</span></div>
    </div>
  );
}

function Bucket({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="card p-4">
      <h5 className={`text-sm font-bold mb-2 ${color}`}>{title}</h5>
      <ul className="text-sm space-y-1.5">
        {items.map((it, i) => <li key={i} className="text-slate-300 flex gap-2">• <span>{it}</span></li>)}
      </ul>
    </div>
  );
}

// ============================================================================
// RANK TRACKER
// ============================================================================
function RankTracker({ onSpent }) {
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [pname, setPname] = useState("");
  const [purl, setPurl] = useState("");
  const [pkws, setPkws] = useState("");
  const [pcountry, setPcountry] = useState("US");
  const [running, setRunning] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(null);

  const load = () => api.get("/seo/tracker/projects").then(r => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    const keywords = pkws.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (!pname.trim() || !purl.trim() || keywords.length === 0) {
      toast.error("Fill project name, URL, and at least one keyword"); return;
    }
    setBusy(true);
    try {
      await api.post("/seo/tracker/projects", {
        project_name: pname.trim(), target_url: purl.trim(),
        keywords, country: pcountry,
      });
      toast.success("Project created");
      setShowNew(false); setPname(""); setPurl(""); setPkws("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runProject = async (pid) => {
    setRunning(pid);
    try {
      await api.post(`/seo/tracker/projects/${pid}/run`);
      toast.success("Rank check complete");
      load(); onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(null); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete tracking project and all history?")) return;
    try { await api.delete(`/seo/tracker/projects/${pid}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="seo-tracker-panel">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">Track keyword rankings over time. 1 credit per keyword per run.</p>
        <button onClick={() => setShowNew(true)} className="btn-primary !py-2 !px-3 text-sm flex items-center gap-1.5" data-testid="tracker-new-btn">
          <Plus size={14}/> New project
        </button>
      </div>

      {showNew && (
        <div className="card p-5 space-y-3" data-testid="tracker-new-form">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-bold">New tracking project</h3>
            <button onClick={()=>setShowNew(false)}><X className="text-slate-400" size={16}/></button>
          </div>
          <input value={pname} onChange={e=>setPname(e.target.value)} placeholder="Project name" className="input w-full" data-testid="tracker-name"/>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={purl} onChange={e=>setPurl(e.target.value)} placeholder="Target URL or domain" className="input" data-testid="tracker-url"/>
            <select value={pcountry} onChange={e=>setPcountry(e.target.value)} className="input">
              {["US","IN","GB","CA","AU","DE","FR","BR","JP","SG"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <textarea value={pkws} onChange={e=>setPkws(e.target.value)} rows={4}
                    placeholder="Keywords — one per line or comma-separated (max 50)"
                    className="input w-full font-mono" data-testid="tracker-keywords"/>
          <button onClick={create} disabled={busy} className="btn-primary" data-testid="tracker-create-btn">
            {busy ? <Loader2 className="animate-spin" size={15}/> : "Create"}
          </button>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="card p-10 text-center text-slate-400">No tracking projects yet.</div>
      ) : (
        <div className="space-y-3" data-testid="tracker-projects-list">
          {projects.map(p => (
            <div key={p.id} className="card p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="text-white font-bold">{p.project_name}</div>
                  <div className="text-xs text-slate-400">{p.domain} · {p.country} · {p.keywords.length} keywords</div>
                </div>
                <div className="flex gap-2 items-center">
                  {p.latest_run && (
                    <div className="text-xs text-right">
                      <div className="text-slate-400">avg rank</div>
                      <div className="text-white font-bold">{p.latest_run.avg_position ?? "—"}</div>
                    </div>
                  )}
                  <button onClick={()=>setHistoryOpen(p)} className="btn-ghost !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`tracker-history-${p.id}`}>
                    <LineChart size={12}/> History
                  </button>
                  <button onClick={()=>runProject(p.id)} disabled={running===p.id}
                          className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`tracker-run-${p.id}`}>
                    {running===p.id ? <Loader2 className="animate-spin" size={13}/> : `Run · ${p.keywords.length} cr`}
                  </button>
                  <button onClick={()=>remove(p.id)} className="text-rose-300 hover:text-rose-200 p-1.5"><Trash2 size={14}/></button>
                </div>
              </div>

              {p.latest_run?.rankings && (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left py-1">Keyword</th><th className="text-right">Rank</th><th className="text-left pl-3">Top 3 competitors</th></tr>
                    </thead>
                    <tbody>
                      {p.latest_run.rankings.map((r, i) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="py-1.5 text-slate-200">{r.keyword}</td>
                          <td className="text-right">
                            {r.position ? (
                              <span className={`px-2 py-0.5 rounded text-xs ${r.position<=3?"bg-emerald-500/20 text-emerald-300":r.position<=10?"bg-amber-500/20 text-amber-300":"bg-slate-700 text-slate-300"}`}>
                                #{r.position}
                              </span>
                            ) : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="pl-3 text-slate-400 text-xs truncate">{(r.top_3 || []).join(" · ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {historyOpen && <RankHistoryModal project={historyOpen} onClose={() => setHistoryOpen(null)}/>}
    </div>
  );
}

function RankHistoryModal({ project, onClose }) {
  const [runs, setRuns] = useState(null);
  const [selectedKw, setSelectedKw] = useState(null);

  useEffect(() => {
    api.get(`/seo/tracker/projects/${project.id}/history?limit=30`).then(r => {
      const fetched = r.data.runs || [];
      setRuns(fetched);
      // Prefer a keyword that has at least one ranking data point
      const kwWithData = project.keywords.find(kw =>
        fetched.some(run => (run.rankings || []).some(row => row.keyword === kw && row.position))
      );
      setSelectedKw(kwWithData || (project.keywords && project.keywords[0]) || null);
    }).catch(() => setRuns([]));
  }, [project.id, project.keywords]);

  // Build series for the chosen keyword across runs (newest first → reverse for chart)
  const series = (runs || []).slice().reverse().map(run => {
    const row = (run.rankings || []).find(r => r.keyword === selectedKw);
    return {
      date: new Date(run.created_at).toLocaleDateString(),
      position: row?.position ?? null,
    };
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="rank-history-modal" onClick={onClose}>
      <div className="card max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">{project.project_name}</h3>
            <div className="text-xs text-slate-400">{project.domain} · {project.country}</div>
          </div>
          <button onClick={onClose}><X className="text-slate-400" size={18}/></button>
        </div>

        {!runs ? (
          <div className="text-center py-8 text-slate-400"><Loader2 className="animate-spin inline" size={18}/></div>
        ) : runs.length === 0 ? (
          <div className="text-center py-8 text-slate-400">No history yet. Run the tracker to start building a timeline.</div>
        ) : (
          <>
            <div className="flex gap-2 flex-wrap mb-4" data-testid="rank-history-kw-chips">
              {project.keywords.map(kw => (
                <button
                  key={kw}
                  onClick={() => setSelectedKw(kw)}
                  className={`text-xs px-2.5 py-1 rounded-full ${
                    selectedKw === kw ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                  data-testid={`rank-history-kw-${kw.replace(/\s+/g,'-')}`}
                >
                  {kw}
                </button>
              ))}
            </div>
            <RankSparkline series={series} keyword={selectedKw}/>
            <div className="text-xs text-slate-500 mt-4 text-center">
              {runs.length} run{runs.length === 1 ? "" : "s"} · {project.keywords.length} keyword{project.keywords.length === 1 ? "" : "s"}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RankSparkline({ series, keyword }) {
  // SVG sparkline — Google ranks: lower is better, so invert Y
  const W = 720, H = 220, pad = 30;
  const points = series.filter(p => p.position !== null);
  if (points.length === 0) {
    return <div className="text-center py-10 text-slate-400 text-sm">No ranking data for <code>{keyword}</code> yet.</div>;
  }
  const minP = 1, maxP = Math.max(...points.map(p => p.position), 50);
  const x = (i) => pad + (i / Math.max(1, series.length - 1)) * (W - pad * 2);
  const y = (p) => pad + ((p - minP) / (maxP - minP)) * (H - pad * 2);
  const path = series.map((p, i) =>
    p.position === null
      ? null
      : `${i === 0 ? "M" : "L"}${x(i)},${y(p.position)}`
  ).filter(Boolean).join(" ");

  return (
    <div className="card p-4" data-testid="rank-sparkline">
      <div className="text-xs text-slate-400 mb-2">Position over time · <span className="text-white font-semibold">{keyword}</span> (lower is better)</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {/* Reference lines: top-3, top-10 */}
        <line x1={pad} x2={W - pad} y1={y(3)}  y2={y(3)}  stroke="#10b981" strokeWidth="0.5" strokeDasharray="4 4"/>
        <line x1={pad} x2={W - pad} y1={y(10)} y2={y(10)} stroke="#f59e0b" strokeWidth="0.5" strokeDasharray="4 4"/>
        <text x={W - pad - 4} y={y(3) - 4} fill="#10b981" fontSize="9" textAnchor="end">Top 3</text>
        <text x={W - pad - 4} y={y(10) - 4} fill="#f59e0b" fontSize="9" textAnchor="end">Top 10</text>
        {/* Path */}
        <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2"/>
        {/* Points */}
        {series.map((p, i) => p.position !== null && (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.position)} r="3.5" fill="#3b82f6" stroke="#fff" strokeWidth="1"/>
            <text x={x(i)} y={y(p.position) - 8} fill="#cbd5e1" fontSize="9" textAnchor="middle">#{p.position}</text>
          </g>
        ))}
        {/* X-axis labels */}
        {series.map((p, i) => i % Math.max(1, Math.floor(series.length / 6)) === 0 && (
          <text key={i} x={x(i)} y={H - 8} fill="#64748b" fontSize="9" textAnchor="middle">{p.date}</text>
        ))}
      </svg>
    </div>
  );
}

// ============================================================================
// SITE CRAWLER
// ============================================================================
function SiteCrawler({ onSpent }) {
  const [url, setUrl] = useState("");
  const [max, setMax] = useState(50);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/seo/crawl/jobs/me").then(r => setJobs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!url.trim()) { toast.error("Enter a URL"); return; }
    setBusy(true);
    try {
      await api.post("/seo/crawl/start", { start_url: url.trim(), max_urls: Number(max), same_domain_only: true });
      toast.success("Crawl started — runs in background");
      setUrl(""); load(); onSpent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openJob = async (id) => {
    try { const { data } = await api.get(`/seo/crawl/jobs/${id}`); setOpen(data); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="seo-crawler-panel">
      <div className="card p-5 flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="label">Start URL</label>
          <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://example.com" className="input w-full" data-testid="crawl-url"/>
        </div>
        <div className="w-32">
          <label className="label">Max URLs</label>
          <select value={max} onChange={e=>setMax(e.target.value)} className="input w-full" data-testid="crawl-max">
            {[10, 25, 50, 100, 250, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button onClick={start} disabled={busy} className="btn-primary" data-testid="crawl-start-btn">
          {busy ? <Loader2 className="animate-spin" size={15}/> : "Start crawl · 10 cr"}
        </button>
      </div>

      {jobs.length > 0 && (
        <div className="card p-5">
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-3">Crawl jobs</h3>
          <ul className="divide-y divide-slate-800" data-testid="crawl-jobs">
            {jobs.map(j => (
              <li key={j.id} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{j.start_url}</div>
                  <div className="text-xs text-slate-500">{j.status === "complete" ? `Crawled ${j.summary?.crawled ?? "—"} pages` : "Running…"}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded ${j.status==="complete"?"bg-emerald-500/20 text-emerald-300":"bg-amber-500/20 text-amber-300"}`}>{j.status}</span>
                <button onClick={()=>openJob(j.id)} className="btn-ghost !py-1 !px-3 text-xs" data-testid={`crawl-open-${j.id}`}>Open</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && (
        <div className="card p-5 space-y-4" data-testid="crawl-detail">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold truncate">{open.start_url}</h3>
            <button onClick={()=>setOpen(null)}><X size={16} className="text-slate-400"/></button>
          </div>
          {open.summary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Crawled" value={open.summary.crawled}/>
                <Stat label="Broken" value={open.summary.broken_count ?? 0}/>
                <Stat label="Redirects" value={open.summary.redirect_count ?? 0}/>
                <Stat label="Missing alt" value={open.summary.images_missing_alt_total ?? 0}/>
              </div>
              <StatusCodeChart counts={open.summary.status_counts || {}}/>
            </>
          )}

          {open.broken && open.broken.length > 0 && (
            <details className="card !bg-rose-500/5 !border-rose-500/30 p-4" data-testid="crawl-broken-list" open>
              <summary className="cursor-pointer text-rose-300 font-semibold text-sm">
                {open.broken.length} broken / errored URL{open.broken.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-3 space-y-1 text-xs">
                {open.broken.slice(0, 50).map((b, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status >= 500 ? "bg-rose-600/40" : b.status >= 400 ? "bg-rose-500/30" : "bg-slate-700"} text-rose-200`}>
                      {b.status || "ERR"}
                    </span>
                    <a href={b.url} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-rose-300 truncate flex-1">{b.url}</a>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {open.redirects && open.redirects.length > 0 && (
            <details className="card !bg-amber-500/5 !border-amber-500/30 p-4" data-testid="crawl-redirects-list">
              <summary className="cursor-pointer text-amber-300 font-semibold text-sm">
                {open.redirects.length} redirect chain{open.redirects.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-3 space-y-2 text-xs">
                {open.redirects.slice(0, 30).map((r, i) => (
                  <li key={i} className="space-y-0.5">
                    <div className="text-slate-300 truncate">From: <span className="text-amber-200">{r.from}</span></div>
                    <div className="text-slate-500 ml-3">→ {r.to} <span className="text-slate-600">({r.final_status})</span></div>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {open.pages && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2">URL</th>
                    <th className="text-center">Code</th>
                    <th className="text-left">Title</th>
                    <th className="text-right">Imgs / No-alt</th>
                  </tr>
                </thead>
                <tbody>
                  {open.pages.slice(0, 200).map((p, i) => (
                    <tr key={i} className="border-t border-slate-800">
                      <td className="p-2 text-slate-200 max-w-[280px] truncate"><a href={p.url} target="_blank" rel="noreferrer" className="hover:text-blue-300">{p.url}</a></td>
                      <td className="text-center">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          p.status >= 200 && p.status < 300 ? "bg-emerald-500/20 text-emerald-300" :
                          p.status >= 300 && p.status < 400 ? "bg-amber-500/20 text-amber-300" :
                          p.status >= 400 ? "bg-rose-500/20 text-rose-300" : "bg-slate-700 text-slate-300"
                        }`}>{p.status || "ERR"}</span>
                      </td>
                      <td className="text-slate-300 max-w-[260px] truncate">{p.title || "—"}</td>
                      <td className="text-right text-slate-400">{p.images ?? 0} / {p.images_missing_alt ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusCodeChart({ counts }) {
  const entries = [
    { label: "2xx", v: counts["2xx"] || 0, color: "bg-emerald-500" },
    { label: "3xx", v: counts["3xx"] || 0, color: "bg-amber-500" },
    { label: "4xx", v: counts["4xx"] || 0, color: "bg-orange-500" },
    { label: "5xx", v: counts["5xx"] || 0, color: "bg-rose-500" },
    { label: "errors", v: counts.errors || 0, color: "bg-slate-500" },
  ];
  const total = entries.reduce((s, e) => s + e.v, 0) || 1;
  return (
    <div className="card p-4" data-testid="crawl-status-chart">
      <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">HTTP status distribution</div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-800">
        {entries.map(e => e.v > 0 && (
          <div key={e.label} className={e.color}
               style={{ width: `${(e.v / total) * 100}%` }}
               title={`${e.label}: ${e.v}`}/>
        ))}
      </div>
      <div className="flex justify-between text-xs mt-2 text-slate-400">
        {entries.map(e => (
          <div key={e.label} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${e.color}`}/>
            <span>{e.label} <span className="text-white font-bold ml-1">{e.v}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}
